import { FC, useMemo, useState } from 'react';
import { Checkbox, Search } from '@carbon/react';
import { ChevronRight } from '@carbon/react/icons';
import classNames from 'classnames';
import styles from './checkbox-tree.module.scss';

export interface CheckboxTreeItem {
  id: number | string;
  label: string;
}

export interface CheckboxTreeGroup {
  id: number | string;
  label: string;
  /** Short code shown right-aligned on the group row, e.g. a state abbreviation. */
  code?: string;
  items: CheckboxTreeItem[];
}

interface Props {
  groups: CheckboxTreeGroup[];
  /** Ids of selected *leaves*. Groups are never selected in their own right — a
   * group's checkbox is a shorthand for its children, which is what keeps the
   * value a flat, unambiguous list. */
  selectedIds: (number | string)[];
  onChange: (ids: (number | string)[]) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  /** Renders the count line above the tree, e.g. "35 counties across 7 states". */
  renderSummary?: (selectedCount: number, groupCount: number) => string;
  emptyMessage?: string;
  disabled?: boolean;
}

const matches = (text: string, term: string) =>
  text.toLowerCase().includes(term);

export const CheckboxTree: FC<Props> = ({
  groups,
  selectedIds,
  onChange,
  searchPlaceholder = 'Search…',
  searchLabel = 'Search',
  renderSummary,
  emptyMessage = 'Nothing matches your search.',
  disabled = false,
}) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number | string>>(new Set());

  // Membership is checked per leaf on every render, so a Set beats repeated
  // Array#includes once a group runs to a few hundred children.
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const term = search.trim().toLowerCase();

  // A group matching by name keeps all its children, so searching a state shows
  // everything in it rather than nothing.
  //
  // The full group travels alongside the filtered rows: the group's checkbox and
  // count always speak for its whole membership, never just what the current
  // search happens to show. Acting on the visible subset would let a search
  // silently narrow a "select all" the user believes they made.
  const visible = useMemo(() => {
    if (!term) return groups.map((group) => ({ group, items: group.items }));

    return groups
      .map((group) => ({
        group,
        items:
          matches(group.label, term) || matches(group.code ?? '', term)
            ? group.items
            : group.items.filter((i) => matches(i.label, term)),
      }))
      .filter(({ items }) => items.length > 0);
  }, [groups, term]);

  // Searching implies you want to see the hits, so matched groups open without
  // disturbing what the user expanded by hand.
  const isExpanded = (id: number | string) => (term ? true : expanded.has(id));

  const toggleExpanded = (id: number | string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const groupState = (group: CheckboxTreeGroup) => {
    const total = group.items.length;
    const count = group.items.reduce(
      (acc, item) => acc + (selected.has(item.id) ? 1 : 0),
      0
    );
    return { count, total, all: total > 0 && count === total, some: count > 0 };
  };

  const setLeaves = (ids: (number | string)[], on: boolean) => {
    const next = new Set(selected);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange([...next]);
  };

  const selectedCount = selected.size;
  const groupsWithSelection = groups.filter((g) =>
    g.items.some((i) => selected.has(i.id))
  ).length;

  return (
    <div className={styles.tree}>
      <Search
        size="lg"
        labelText={searchLabel}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        disabled={disabled}
      />

      <div className={styles.summaryRow}>
        <span className={styles.summary}>
          {renderSummary
            ? renderSummary(selectedCount, groupsWithSelection)
            : `${selectedCount} selected`}
        </span>
        <button
          type="button"
          className={styles.clear}
          onClick={() => onChange([])}
          disabled={disabled || selectedCount === 0}
        >
          Clear selection
        </button>
      </div>

      <div className={styles.scroll}>
        {visible.length === 0 && <p className={styles.empty}>{emptyMessage}</p>}

        {visible.map(({ group, items }) => {
          const { count, total, all, some } = groupState(group);
          const open = isExpanded(group.id);

          return (
            <div key={group.id} className={styles.group}>
              <div className={styles.groupRow}>
                <button
                  type="button"
                  className={styles.caret}
                  aria-expanded={open}
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${group.label}`}
                  onClick={() => toggleExpanded(group.id)}
                >
                  <ChevronRight
                    className={classNames(styles.caretIcon, {
                      [styles.caretOpen]: open,
                    })}
                  />
                </button>

                <Checkbox
                  id={`tree-group-${group.id}`}
                  labelText={group.label}
                  className={styles.groupCheckbox}
                  checked={all}
                  indeterminate={!all && some}
                  disabled={disabled}
                  onChange={(_e, { checked }) =>
                    setLeaves(
                      group.items.map((i) => i.id),
                      checked
                    )
                  }
                />

                {group.code && (
                  <span className={styles.code}>{group.code}</span>
                )}
                <span
                  className={classNames(styles.count, {
                    [styles.countActive]: some,
                  })}
                >
                  {count}/{total}
                </span>
              </div>

              {open && (
                <div className={styles.items}>
                  {items.map((item) => (
                    <Checkbox
                      key={item.id}
                      id={`tree-item-${item.id}`}
                      labelText={item.label}
                      checked={selected.has(item.id)}
                      disabled={disabled}
                      onChange={(_e, { checked }) =>
                        setLeaves([item.id], checked)
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
